import {
  CROSS_FOLDER_SORTS,
  DEFAULT_CROSS_FOLDER_SORT,
  type Folder,
  type MessageSortOrder,
  type SearchScope,
} from '@/api/types';

/**
 * Where a search looks, as pure functions (Gmail's "search all mail").
 *
 * The scope lives in the URL beside `?q=`, so opening a result, Back and a
 * reload all keep the same list of results: `?scope=all|folder` and `?trash=1`
 * for "include Trash & Junk". When the URL does not say, the choice the person
 * made last in this browser session wins, and before any choice the Inbox
 * searches everything (as Gmail does) while any other folder searches itself —
 * someone who opened Receipts and typed there meant Receipts.
 */

export const SCOPE_PARAM = 'scope';
export const TRASH_PARAM = 'trash';

/** sessionStorage: remembered for the session, forgotten with the tab. */
export const SCOPE_SESSION_KEY = 'mail.searchScope';

/** `in:sent`, `in:anywhere`… — the query's own terms pick the folders. */
export function hasInOperator(query: string): boolean {
  return /(^|\s)in:\S/i.test(query);
}

export function readRememberedScope(): SearchScope | undefined {
  try {
    const value = window.sessionStorage.getItem(SCOPE_SESSION_KEY);
    return value === 'all' || value === 'folder' ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberScope(scope: SearchScope): void {
  try {
    window.sessionStorage.setItem(SCOPE_SESSION_KEY, scope);
  } catch {
    // Private mode or blocked storage: the URL still carries this choice.
  }
}

/**
 * The scope in force: the URL's, else the session's, else by folder.
 *
 * `role` is the role of the folder the search started from; `undefined` is
 * the merged view, whose lanes are Inboxes.
 */
export function resolveSearchScope(
  param: string | null,
  role: Folder['role'] | undefined,
  remembered: SearchScope | undefined = readRememberedScope(),
): SearchScope {
  if (param === 'all' || param === 'folder') return param;
  if (remembered !== undefined) return remembered;
  return role === undefined || role === 'inbox' ? 'all' : 'folder';
}

/**
 * A cross-folder search is served only in the orders the server can merge
 * (`CROSS_FOLDER_SORTS`); anything else is sent as the `Date:` order rather
 * than as a request the server would refuse.
 */
export function clampCrossFolderSort(sort: MessageSortOrder): MessageSortOrder {
  return CROSS_FOLDER_SORTS.includes(sort) ? sort : DEFAULT_CROSS_FOLDER_SORT;
}

/**
 * The list's Unread / Starred chips, spelled as the search's own `is:` terms.
 * The search endpoint takes one query line and no flag parameters, and these
 * are the same keys the folder listing sends as `unread_only`/`flagged_only`.
 */
export function withFilterTerms(query: string, filter: 'all' | 'unread' | 'flagged'): string {
  if (filter === 'unread') return `${query} is:unread`;
  if (filter === 'flagged') return `${query} is:starred`;
  return query;
}

/**
 * The folder a set of rows shares, or `undefined` when they span several.
 *
 * Bulk verbs that depend on "the folder you are in" (no Archive inside
 * Archive, no Move to the folder itself) ask the ROWS in a cross-folder list:
 * the route's folder is only where the search started.
 */
export function commonFolderOf(rows: readonly { folderId: string }[]): string | undefined {
  const [first, ...rest] = rows;
  if (first === undefined) return undefined;
  return rest.every((row) => row.folderId === first.folderId) ? first.folderId : undefined;
}

/**
 * The partial-results sentence: which folders timed out, which failed, and
 * that the rows shown are therefore not all of them.
 */
export function describeSearchGaps<T extends { reason: 'timeout' | 'failed' }>(
  gaps: readonly T[],
  nameOf: (gap: T) => string,
): string {
  const timedOut = gaps.filter((gap) => gap.reason === 'timeout').map(nameOf);
  const failed = gaps.filter((gap) => gap.reason === 'failed').map(nameOf);
  const parts: string[] = [];
  if (timedOut.length > 0) parts.push(`Some folders timed out: ${timedOut.join(', ')}.`);
  if (failed.length > 0) parts.push(`Could not search: ${failed.join(', ')}.`);
  if (parts.length === 0) parts.push('Some folders could not be searched.');
  parts.push('Results may be incomplete.');
  return parts.join(' ');
}

/** Search params that belong to the result list, carried when a result is opened. */
export function listSearchParams(params: URLSearchParams): string {
  const out = new URLSearchParams();
  // Without a query there is no result list to keep, only a folder.
  if ((params.get('q') ?? '').trim() === '') return '';
  for (const name of ['q', SCOPE_PARAM, TRASH_PARAM]) {
    const value = params.get(name);
    if (value !== null && value !== '') out.set(name, value);
  }
  const text = out.toString();
  return text === '' ? '' : `?${text}`;
}
